import React, { useEffect, useState } from "react"
import { MailOutlined } from "@ant-design/icons"
import { Button, Modal, DatePicker, Checkbox, message, Tooltip, Switch, Input, Divider, Typography, Select } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { gen, swimminglyApi } from "./utils"
import { toIsoStringUTC, parseIsoToLocal, formatForDisplay } from "./utils/TimeHelper"


function disabledDate(current: any): boolean {
  if (!current) return false
  const md =
    current && typeof current.toDate === "function"
      ? dayjs(current.toDate())
      : dayjs(current)
  // Legacy helper used by older button variants: disable days before today
  return md.isBefore(dayjs().startOf("day"))
}

function isNonProdClientEnv(): boolean {
  if (typeof window === "undefined") return false
  const host = window.location.hostname || ""
  const isDev = host === "localhost" || host === "127.0.0.1"
  const isStaging = host.includes("swimminglytesting.ninja")
  return isDev || isStaging
}

function getApiErrorMessage(data: any, fallbackMessage: string): string {
  return data?.error || data?.message || fallbackMessage
}

export function MeetEntryButtonOpen({
  meetId,
  clubId,
  userId,
  className,
  refreshPage,
}: {
  meetId: number
  clubId: number
  userId: number
  className: string
  refreshPage: () => void
}) {
  const [visible, setVisible] = useState(false)
  const [closeDate, setCloseDate] = useState<Dayjs | null>(null)
  const [meetDeclarationsOpen, setMeetDeclarationsOpen] = useState(false)
  const [testOverrideEmail, setTestOverrideEmail] = useState("")
  const [sendEmailNow, setSendEmailNow] = useState(true)
  const [enableReminder, setEnableReminder] = useState(true)
  const [reminderOffsetHours, setReminderOffsetHours] = useState<number>(48)

  const nonProdClient = isNonProdClientEnv()

  const clickOk = () => {
    if (!closeDate) {
      message.error("Please select a deadline date and time.")
      return
    }

    const minAllowed = dayjs().add(1, "hour")
    if (closeDate.isBefore(minAllowed)) {
      message.error("Please choose a deadline at least 1 hour from now.")
      return
    }

    // Convert selected local Dayjs to UTC formatted string without timezone plugins
    const finalCloseDate = toIsoStringUTC(closeDate) // send ISO/Z

    let overrideAddress: string | undefined
    if (nonProdClient && testOverrideEmail.trim()) {
      const trimmed = testOverrideEmail.trim()
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      if (!isValid) {
        message.error("Please enter a valid email address.")
        return
      }
      overrideAddress = trimmed
    }

    swimminglyApi
      .post(gen("/api/openMeetEntry"))
      .body({
        meetDeclarationsCloseDate: finalCloseDate,
        meetDeclarationsOpen,
        meetId,
        clubId,
        userId,
        sendEmailNow,
        enableReminder,
        reminderOffsetHours,
        testOverrideToAddress: overrideAddress,
      })
      .then((data) => {
        if (data.error || data.status === "error") {
          console.log(data)
          message.error(
            getApiErrorMessage(data, "Uh oh, something went wrong"),
          )
        } else {
          message.success("Opened meet entries!")
          setVisible(false)
          refreshPage()
        }
      })
  }
  return (
    <React.Fragment>
      <Button
        className={className}
        onClick={() => setVisible(true)}
        key="MeetEntryModal"
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto",
          justifyContent: "center",
          columnGap: "10px",
          alignItems: "center",
          padding: "2px",
          backgroundColor: "#52C41B",
          color: "white",
        }}
      >
        <MailOutlined />
        <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
          <span style={{ height: "1.2em" }}>Meet Declarations</span>
          <span style={{ height: "1.2em" }}>Open to parents now</span>
        </div>
      </Button>
      <Modal
        title="Open Meet To Parents"
        open={visible}
        okText="Save meet declarations"
        // disabledDate={disabledDate}
        onOk={clickOk}
        onCancel={() => setVisible(false)}
      >
        Have parents declare their swimmers for this meet! Open to parent
        guardians on your roster, this will trigger an email to your parents and
        open declarations for this swim meet.
        <br />
        <br />
        Note: Your club leadership retain's final review &amp; approval of
        entries. <br />
        <br />
        <ol>
          <li>
            You will open this meet to declarations below. This will allow
            parents to attend/decline the meet by swimmer in the Clubhouse. You
            will see the status of each swimmer under View Meet Lineup.
          </li>
          <li>
            If you want to also allow parents to enter swimmers in specific
            events, check that feature below. Note that you will retain
            responsibility over entering relays. Parents will see the entry
            limit designated for this swim meet.
          </li>
          <li>
            Once opened, parents will receive an email to complete
            declarations/entries as well as a friendly reminder 48 hours before
            the deadline closes that you set below.
          </li>
          <li>
            We recommend setting a deadline far enough in advance that allows
            your team leadership to make any revisions.
          </li>
        </ol>
        <Checkbox
          checked={meetDeclarationsOpen}
          onChange={(e) => setMeetDeclarationsOpen(e.target.checked)}
        >
          Allow parents to enter their swimmers in individual events
        </Checkbox>
        <br />
        <br />
        <DatePicker
          format="MM-DD-YYYY hh:mm a"
          showTime={{ format: "hh:mm a", minuteStep: 15 } as any}
          showNow={false}
          placeholder="Select Meet Entry Closing Date Time"
          style={{ width: "400px" }}
          disabledDate={disabledDate}
          value={closeDate}
          onChange={(val) => setCloseDate(val || null)}
        />
        <Divider />
        <div style={{ marginBottom: 8 }}>
          <strong>Email notifications</strong>
        </div>
        <Checkbox
          checked={sendEmailNow}
          onChange={(e) => setSendEmailNow(e.target.checked)}
        >
          Send announcement email to parents now (recommended)
        </Checkbox>
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="secondary">
            Recommended so parents know about this meet and deadline.
          </Typography.Text>
        </div>
        <br />
        <Checkbox
          checked={enableReminder}
          onChange={(e) => setEnableReminder(e.target.checked)}
        >
          Send automatic reminder before the deadline
        </Checkbox>
        {enableReminder && (
          <div style={{ marginLeft: 24, marginTop: 4 }}>
            <span style={{ marginRight: 8 }}>Reminder timing:</span>
            <Select
              size="small"
              style={{ width: "fit-content" }}
              value={reminderOffsetHours}
              onChange={setReminderOffsetHours}
              options={[
                { value: 24, label: "24 hours before" },
                { value: 48, label: "48 hours before (recommended)" },
                { value: 72, label: "72 hours before" },
              ]}
            />
          </div>
        )}
        {nonProdClient && (
          <>
            <br />
            <br />
            <p style={{ fontSize: 12 }}>
              In testing environments, you can direct this notification to a single
              QA email instead of real parents. Leave blank to use the default
              override address.
            </p>
            <Input
              placeholder="Optional test recipient email (non-production only)"
              value={testOverrideEmail}
              onChange={(e) => setTestOverrideEmail(e.target.value)}
            />
          </>
        )}
      </Modal>
    </React.Fragment>
  )
}

export function MeetEntryButtonInProgress({
  meetDeclarationsCloseDate,
  refreshPage,
  className,
  meetId,
  clubId,
  userId,
  parentEntries,
}: {
  meetDeclarationsCloseDate: string | null
  refreshPage: () => void
  className: string
  meetId: number
  clubId: number
  userId: number
  parentEntries: boolean
}) {
  const [visible, setVisible] = useState(false)
  const [closeDate, setCloseDate] = useState<Dayjs | null>(
    meetDeclarationsCloseDate && typeof meetDeclarationsCloseDate === "string" && meetDeclarationsCloseDate.trim() !== ""
      ? dayjs(meetDeclarationsCloseDate)
      : null,
  )
  const [meetDeclarationsOpen, setMeetDeclarationsOpen] =
    useState(parentEntries)
  const [testOverrideEmail, setTestOverrideEmail] = useState("")
  const [sendEmailNow, setSendEmailNow] = useState(true)
  const [enableReminder, setEnableReminder] = useState(true)
  const [reminderOffsetHours, setReminderOffsetHours] = useState<number>(48)

  const nonProdClient = isNonProdClientEnv()

  useEffect(() => {
    setMeetDeclarationsOpen(parentEntries)
  }, [meetId, clubId, parentEntries])

  useEffect(() => {
    setCloseDate(parseIsoToLocal(meetDeclarationsCloseDate))
  }, [meetDeclarationsCloseDate])

  const clickOk = () => {
    if (!closeDate) {
      message.error("Please select a deadline date and time.")
      return
    }

    const minAllowed = dayjs().add(1, "hour")
    if (closeDate.isBefore(minAllowed)) {
      message.error("Please choose a deadline at least 1 hour from now.")
      return
    }

    const finalCloseDate = toIsoStringUTC(closeDate)
    let overrideAddress: string | undefined
    if (nonProdClient && testOverrideEmail.trim()) {
      const trimmed = testOverrideEmail.trim()
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      if (!isValid) {
        message.error("Please enter a valid email address.")
        return
      }
      overrideAddress = trimmed
    }

    swimminglyApi
      .post(gen("/api/inProgressMeetEntry"))
      .body({
        closeDate: finalCloseDate,
        meetDeclarationsOpen,
        meetId,
        clubId,
        userId,
        sendEmailNow,
        enableReminder,
        reminderOffsetHours,
        testOverrideToAddress: overrideAddress,
      })
      .then((data) => {
        if (data.error) {
          message.error(data.error)
        } else {
          setVisible(false)
          refreshPage()
        }
      })
  }

  return (
    <React.Fragment>
      <Button
        className={className}
        onClick={() => setVisible(true)}
        key="MeetEntryModal"
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto",
          justifyContent: "center",
          columnGap: "10px",
          alignItems: "center",
          padding: "2px",
          backgroundColor: "#FA8B14",
          color: "white",
        }}
      >
        <MailOutlined />
        <Tooltip
          title="As an admin, you can continue to make revisions to entries even after the deadline has closed."
          color="#101242"
        >
        <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
          <span style={{ height: "1.2em" }}>Meet Declarations Deadline</span>
          <span style={{ height: "1.2em" }}>
            {formatForDisplay(closeDate, "M/D h:mm a")}
          </span>
        </div>
        </Tooltip>
      </Button>
      <Modal
        title="Update Meet Declarations Deadline?"
        open={visible}
        okText="Save meet declarations"
        onOk={clickOk}
        onCancel={() => setVisible(false)}
      >
        Updating your meet declarations deadline will not interrupt the meet declarations
        currently in progress.
        <br />
        <br />
        Note: Your club leadership retain's final review &amp; approval of
        entries. <br />
        <br />
        <ol>
          <li>
            Meet Declarations (attending/not attending) will be visible under your Entries in your 'By Swimmer' view.
          </li>
          <li>
            Change your mind? You can enable parents to enter swimmers
            directly into invidual events below by clicking 'Allow parents to
            enter their swimmers in individual events'. Note that you will retain
            responsibility over entering relays. Parents will see the entry
            limit designated for this swim meet.
          </li>
          <li>
            Parent/Guardians will receive a friendly email reminder 48 hours before
            the deadline closes that you update below.
          </li>
          <li>
            We recommend setting a deadline far enough in advance that allows
            your club leadership to make any revisions.
          </li>
        </ol>
        <br />
        <Checkbox
          checked={meetDeclarationsOpen}
          onChange={(e) => setMeetDeclarationsOpen(e.target.checked)}
        >
          Allow parents to enter their swimmers in individual events
        </Checkbox>
        <br />
        <DatePicker
          showTime={{ format: "hh:mm a", minuteStep: 15 } as any}
          showNow={false}
          placeholder="Select Meet Entry Closing Date Time"
          style={{ width: "400px" }}
          disabledDate={disabledDate}
          value={closeDate}
          format="MM-DD-YYYY hh:mm a"
          onChange={(val) => setCloseDate(val || null)}
        />
        <Divider />
        <div style={{ marginBottom: 8 }}>
          <strong>Email notifications</strong>
        </div>
        <Checkbox
          checked={sendEmailNow}
          onChange={(e) => setSendEmailNow(e.target.checked)}
        >
          Send updated email about these changes (recommended)
        </Checkbox>
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="secondary">
            Recommended so parents know about any updated details.
          </Typography.Text>
        </div>
        <br />
        <Checkbox
          checked={enableReminder}
          onChange={(e) => setEnableReminder(e.target.checked)}
        >
          Send automatic reminder before the deadline
        </Checkbox>
        {enableReminder && (
          <div style={{ marginLeft: 24, marginTop: 4 }}>
            <span style={{ marginRight: 8 }}>Reminder timing:</span>
            <Select
              size="small"
              style={{ width: "fit-content" }}
              value={reminderOffsetHours}
              onChange={setReminderOffsetHours}
              options={[
                { value: 24, label: "24 hours before" },
                { value: 48, label: "48 hours before (recommended)" },
                { value: 72, label: "72 hours before" },
              ]}
            />
          </div>
        )}
        {nonProdClient && (
          <>
            <br />
            <br />
            <p style={{ fontSize: 12 }}>
              In testing environments, you can direct this reminder email to a
              single QA address instead of real parents. Leave blank to use the
              default override address.
            </p>
            <Input
              placeholder="Optional test recipient email (non-production only)"
              value={testOverrideEmail}
              onChange={(e) => setTestOverrideEmail(e.target.value)}
            />
          </>
        )}
      </Modal>
    </React.Fragment>
  )
}

export function MeetEntryButtonClosed({
  meetId,
  clubId,
  className,
  refreshPage,
}: {
  meetId: number
  clubId: number
  className: string
  refreshPage: () => void
}): JSX.Element {
  const [visible, setVisible] = useState(false)
  const [numClicks, setNumClicks] = useState(0)
  const [closeDate, setCloseDate] = useState<Dayjs | null>(null)

  useEffect(() => {
    setNumClicks(0)
  }, [visible])

  const clickOk = () => {
    if (numClicks === 0) {
      setNumClicks(numClicks + 1)
      return
    } else if (!closeDate) {
      message.error("Please select a deadline date and time.")
      return
    } else {
      const minAllowed = dayjs().add(1, "hour")
      if (closeDate.isBefore(minAllowed)) {
        message.error("Please choose a deadline at least 1 hour from now.")
        return
      }
    }

    // convert selected local time to canonical ISO/Z for API transport
    const finalCloseDate = toIsoStringUTC(closeDate)
    swimminglyApi
      .post(gen("/api/closedMeetEntry"))
      .body({ meetId, clubId, meetDeclarationsCloseDate: finalCloseDate })
      .then((data) => {
        if (data.status === "success") {
          message.success("reopened meet entries!")
        } else {
          message.error("uh oh, something went wrong reopening meet entries")
        }
      })
      .finally(() => {
        setVisible(false)
        refreshPage()
      })
  }
  return (
    <React.Fragment>
      <Button
        className={className}
        onClick={() => setVisible(true)}
        key="MeetEntryModal"
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto",
          justifyContent: "center",
          columnGap: "10px",
          alignItems: "center",
          padding: "2px",
          backgroundColor: "#8898aa",
          color: "white",
        }}
      >
        <MailOutlined />
        <Tooltip
          title="As an admin, you can continue to make revisions to entries even after the deadline has closed."
          color="#101242"
        >
        <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
          <span style={{ height: "1.2em" }}>Meet Declarations</span>
          <span style={{ height: "1.2em" }}>Closed to parents</span>
        </div>
        </Tooltip>
      </Button>
      <Modal
        title={
          numClicks === 0
            ? "Meet Declarations are Closed"
            : "Reopen Meet Declarations?"
        }
        open={visible}
        okText={
          numClicks === 0
            ? "Reopen Declarations"
            : "Confirm Reopening Declarations"
        }
        onOk={clickOk}
        onCancel={() => setVisible(false)}
      >
        {numClicks === 0 ? (
          <React.Fragment>
            Meet declarations &amp; entries are now closed to parents for this
            meet. As a club admin, you can still revise entries as desired.
            <br />
            <br />
            <ul>
              <li>
                To see the status of each swimmer for this meet, navigate to
                your entries 'By Swimmer' view.
              </li>
              <li>
                Swimmers attending the meet will have a check next to their name
              </li>
              <li>
                Swimmers not attending the meet will be struck through and at
                the bottom of the roster
              </li>
              <li>
                Swimmers who have not been declared will not have a check.
              </li>
              <li>
                If you enabled parents to directly enter their swimmers in individual
                events, you will see those swimmers entered!
              </li>
            </ul>
          </React.Fragment>
        ) : (
          <React.Fragment>
            Meet declarations &amp; entries are now closed to parents for this
            meet. If you would like to reopen them select when you'd like them
            to close and then click the "Confirm Reopening Declarations" button.
            <br />
            <br />
            <DatePicker
                showTime={{ format: "hh:mm a", minuteStep: 15 } as any}
                showNow={false}
                placeholder="Select Meet Entry Closing Date Time"
                style={{ width: "400px" }}
                disabledDate={disabledDate}
                value={closeDate}
                format="MM-DD-YYYY hh:mm a"
                onChange={(val) => setCloseDate(val || null)}
              />
          </React.Fragment>
        )}
        <br />
      </Modal>
    </React.Fragment>
  )
}

export default function MeetEntryButton({
  refreshPage,
  meetId,
  clubId,
  userId,
  className,
  meetDeadlineUpperIso,
  parentAllowIndividualEntries,
  parentAllowRelayAvailability,
  parentAllowComments,
  declarationsLocked,
}: {
  refreshPage: () => void
  meetId: number
  clubId: number
  userId: number
  className: string
  meetDeadlineUpperIso?: string | null
  parentAllowIndividualEntries: boolean
  parentAllowRelayAvailability: boolean
  parentAllowComments: boolean
  declarationsLocked: boolean
}) {
  const [meetDeclarationsCloseDate, setMeetDeclarationsCloseDate] =
    useState<string | null>(null)
  const [triggerRefresh, setTriggerRefresh] = useState(0)
  const [parentEntries, setParentEntries] = useState(false)

  // State for the simple toggle + modals
  const [openModalVisible, setOpenModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [reopenModalVisible, setReopenModalVisible] = useState(false)

  const [closeDate, setCloseDate] = useState<Dayjs | null>(null)
  const [allowIndividualEntries, setAllowIndividualEntries] =
    useState<boolean>(parentAllowIndividualEntries)
  const [allowRelayAvailability, setAllowRelayAvailability] =
    useState<boolean>(parentAllowRelayAvailability)
  const [allowComments, setAllowComments] = useState<boolean>(
    parentAllowComments,
  )
  const [sendEmailNow, setSendEmailNow] = useState(true)
  const [enableReminder, setEnableReminder] = useState(true)
  const [reminderOffsetHours, setReminderOffsetHours] = useState<number>(48)
  const [allowReplies, setAllowReplies] = useState(false)
  const [copyOtherClubAdmins, setCopyOtherClubAdmins] = useState(false)
  const [adminCopyLoading, setAdminCopyLoading] = useState(false)
  const [adminCopyOptions, setAdminCopyOptions] = useState<
    { label: string; value: number; email: string }[]
  >([])
  const [adminCopyUserIds, setAdminCopyUserIds] = useState<number[]>([])

  const refreshData = () => {
    refreshPage()
    setTriggerRefresh((prev) => prev + 1)
  }

  const loadAdminCopyOptions = async () => {
    if (!clubId || !userId) return
    try {
      setAdminCopyLoading(true)
      const response = await swimminglyApi.get(
        gen(
          `/api/meetDeclarations/copyAdmins?clubId=${encodeURIComponent(
            String(clubId),
          )}&userId=${encodeURIComponent(String(userId))}`,
        ),
      )
      const data: any = (response as any)?.data ?? response ?? {}
      const admins: any[] = Array.isArray(data.admins) ? data.admins : []
      setAdminCopyOptions(
        admins.map((admin) => ({
          value: Number(admin.userId),
          label: `${admin.name} (${admin.roleName || "Admin"})`,
          email: String(admin.email || ""),
        })),
      )
    } catch (_err) {
      message.error("Unable to load club admin recipients")
    } finally {
      setAdminCopyLoading(false)
    }
  }

  const handleCopyAdminsToggle = (checked: boolean) => {
    setCopyOtherClubAdmins(checked)
    if (!checked) {
      setAdminCopyUserIds([])
      return
    }
    if (adminCopyOptions.length === 0) {
      void loadAdminCopyOptions()
    }
  }

  const renderSenderAndAdminCopySection = () => (
    <>
      <Divider />
      <div style={{ marginBottom: 8 }}>
        <strong>Sender and Admin Copies</strong>
      </div>
      <Checkbox
        checked={allowReplies}
        onChange={(e) => setAllowReplies(e.target.checked)}
      >
        Allow replies to my email
      </Checkbox>
      <div style={{ marginLeft: 24, marginTop: 4 }}>
        <Typography.Text type="secondary">
          Email still sends from Swimmingly no-reply. When enabled, pressing
          Reply goes to your email.
        </Typography.Text>
      </div>
      {allowReplies && (
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="warning">
            Any copied admins are sent as CC and visible to all recipients.
          </Typography.Text>
        </div>
      )}
      <br />
      <Checkbox
        checked={copyOtherClubAdmins}
        onChange={(e) => handleCopyAdminsToggle(e.target.checked)}
      >
        Copy other club admins
      </Checkbox>
      {copyOtherClubAdmins && (
        <div style={{ marginLeft: 24, marginTop: 8 }}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Select league/club admins to copy"
            style={{ width: "100%" }}
            loading={adminCopyLoading}
            value={adminCopyUserIds}
            onChange={(values) => setAdminCopyUserIds(values as number[])}
            options={adminCopyOptions}
            showSearch={{ optionFilterProp: "label" }}
          />
          <div style={{ marginTop: 4 }}>
            <Typography.Text type="secondary">
              Copied admins are {allowReplies ? "CC'd (visible)" : "BCC'd (hidden)"} on each parent email.
            </Typography.Text>
          </div>
        </div>
      )}
    </>
  )

  // Keep local feature flag state in sync with values
  // coming from the parent modal whenever it refreshes
  useEffect(() => {
    setAllowIndividualEntries(parentAllowIndividualEntries)
  }, [parentAllowIndividualEntries])

  useEffect(() => {
    setAllowRelayAvailability(parentAllowRelayAvailability)
  }, [parentAllowRelayAvailability])

  useEffect(() => {
    setAllowComments(parentAllowComments)
  }, [parentAllowComments])

  useEffect(() => {
    swimminglyApi
      .get(
        gen(
          `/api/getMeetDeclarationsCloseDate/meetId=${meetId}/clubId=${clubId}`,
        ),
      )
      .then((data) => {
        setMeetDeclarationsCloseDate(data.meetDeclarationsCloseDate || null)
        setParentEntries(Boolean(data.parentEntries))
      })
  }, [meetId, clubId, triggerRefresh])

  const hasDeadline = Boolean(meetDeclarationsCloseDate)
  const deadlineLocal =
    meetDeclarationsCloseDate &&
    typeof meetDeclarationsCloseDate === "string" &&
    meetDeclarationsCloseDate.trim() !== ""
      ? parseIsoToLocal(meetDeclarationsCloseDate)
      : null

  // Parents can only treat declarations as "open" when the
  // DB flag is set for this club AND the deadline is in the
  // future. This keeps UI aligned with server behavior.
  const isCurrentlyOpen = !!(
    parentEntries && deadlineLocal && deadlineLocal.isAfter(dayjs())
  )

  const meetDeadlineUpper =
    typeof meetDeadlineUpperIso === "string" &&
    meetDeadlineUpperIso.trim() !== ""
      ? dayjs(meetDeadlineUpperIso)
      : null

  const disabledDateForPicker = (current: any): boolean => {
    if (!current) return false
    const md =
      current && typeof current.toDate === "function"
        ? dayjs(current.toDate())
        : dayjs(current)

    // disallow days before today
    if (md.isBefore(dayjs().startOf("day"))) return true

    // if we know the meet date, disallow days after that
    if (meetDeadlineUpper && md.isAfter(meetDeadlineUpper.endOf("day"))) {
      return true
    }

    return false
  }

  const handleToggleChange = (checked: boolean) => {
    if (declarationsLocked) return

    // Turning ON: open appropriate modal based on whether there is a deadline
    if (checked) {
      if (!hasDeadline) {
        setCloseDate(null)
        setOpenModalVisible(true)
      } else if (deadlineLocal && deadlineLocal.isAfter(dayjs())) {
        // Currently open with a future deadline: let them adjust it
        setCloseDate(deadlineLocal)
        setEditModalVisible(true)
      } else {
        // Previously closed (deadline in the past): reopen with a new deadline
        setCloseDate(null)
        setReopenModalVisible(true)
      }
      return
    }

    // Turning OFF: close declarations early by setting the deadline to now
    if (!hasDeadline) {
      // Nothing to persist; just let the UI reflect closed state
      return
    }

    const finalCloseDate = toIsoStringUTC(dayjs())

    swimminglyApi
      .post(gen("/api/closedMeetEntry"))
      .body({
        meetId,
        clubId,
        meetDeclarationsCloseDate: finalCloseDate,
        // When explicitly closing via the toggle, also clear the
        // parent-entries flag so the DB state reflects "closed".
        meetDeclarationsOpen: 0,
      })
      .then((data) => {
        if (data.status === "success") {
          message.success("Closed meet declarations to parents")
          setMeetDeclarationsCloseDate(finalCloseDate)
          refreshData()
        } else {
          message.error(
            getApiErrorMessage(
              data,
              "Uh oh, something went wrong closing meet declarations",
            ),
          )
        }
      })
      .catch(() => {
        message.error(
          "Uh oh, something went wrong closing meet declarations",
        )
      })
  }

  const handleOpenOk = () => {
    if (!closeDate) {
      message.error("Please select a deadline date and time.")
      return
    }

    const minAllowed = dayjs().add(1, "hour")
    if (closeDate.isBefore(minAllowed)) {
      message.error("Please choose a deadline at least 1 hour from now.")
      return
    }

    if (meetDeadlineUpper && closeDate.isAfter(meetDeadlineUpper)) {
      message.error(
        "Please choose a declarations deadline on or before the allowed deadline for this meet.",
      )
      return
    }

    const finalCloseDate = toIsoStringUTC(closeDate)

    swimminglyApi
      .post(gen("/api/openMeetEntry"))
      .body({
        meetDeclarationsCloseDate: finalCloseDate,
        meetId,
        clubId,
        userId,
        parentAllowIndividualEntries: allowIndividualEntries ? 1 : 0,
        parentAllowRelayAvailability: allowRelayAvailability ? 1 : 0,
        parentAllowComments: allowComments ? 1 : 0,
        sendEmailNow,
        enableReminder,
        reminderOffsetHours,
        allowResponses: allowReplies,
        copyOtherClubAdmins,
        adminCopyUserIds,
      })
      .then((data) => {
        if (data.error || data.status === "error") {
          message.error(
            getApiErrorMessage(data, "Uh oh, something went wrong"),
          )
        } else {
          message.success("Opened meet entries!")
          setOpenModalVisible(false)
          setMeetDeclarationsCloseDate(finalCloseDate)
          refreshData()
        }
      })
  }

  const handleEditOk = () => {
    if (!closeDate) {
      message.error("Please select a deadline date and time.")
      return
    }

    const minAllowed = dayjs().add(1, "hour")
    if (closeDate.isBefore(minAllowed)) {
      message.error("Please choose a deadline at least 1 hour from now.")
      return
    }

    if (meetDeadlineUpper && closeDate.isAfter(meetDeadlineUpper)) {
      message.error(
        "Please choose a declarations deadline on or before the allowed deadline for this meet.",
      )
      return
    }

    const finalCloseDate = toIsoStringUTC(closeDate)

    swimminglyApi
      .post(gen("/api/inProgressMeetEntry"))
      .body({
        closeDate: finalCloseDate,
        meetId,
        clubId,
        userId,
        parentAllowIndividualEntries: allowIndividualEntries ? 1 : 0,
        parentAllowRelayAvailability: allowRelayAvailability ? 1 : 0,
        parentAllowComments: allowComments ? 1 : 0,
        sendEmailNow,
        enableReminder,
        reminderOffsetHours,
        allowResponses: allowReplies,
        copyOtherClubAdmins,
        adminCopyUserIds,
      })
      .then((data) => {
        if (data.error || data.status === "error") {
          message.error(
            getApiErrorMessage(
              data,
              "Uh oh, something went wrong updating meet declarations",
            ),
          )
        } else {
          setEditModalVisible(false)
          setMeetDeclarationsCloseDate(finalCloseDate)
          refreshData()
        }
      })
  }

  const handleReopenOk = () => {
    if (!closeDate) {
      message.error("Please select a deadline date and time.")
      return
    }

    const minAllowed = dayjs().add(1, "hour")
    if (closeDate.isBefore(minAllowed)) {
      message.error("Please choose a deadline at least 1 hour from now.")
      return
    }

    if (meetDeadlineUpper && closeDate.isAfter(meetDeadlineUpper)) {
      message.error(
        "Please choose a declarations deadline on or before the allowed deadline for this meet.",
      )
      return
    }

    const finalCloseDate = toIsoStringUTC(closeDate)

    swimminglyApi
      .post(gen("/api/closedMeetEntry"))
      .body({
        meetId,
        clubId,
        userId,
        meetDeclarationsCloseDate: finalCloseDate,
        meetDeclarationsOpen: 1,
        parentAllowIndividualEntries: allowIndividualEntries ? 1 : 0,
        parentAllowRelayAvailability: allowRelayAvailability ? 1 : 0,
        parentAllowComments: allowComments ? 1 : 0,
        sendEmailNow,
        enableReminder,
        reminderOffsetHours,
        allowResponses: allowReplies,
        copyOtherClubAdmins,
        adminCopyUserIds,
      })
      .then((data) => {
        if (data.status === "success") {
          message.success("Reopened meet declarations!")
          setReopenModalVisible(false)
          setMeetDeclarationsCloseDate(finalCloseDate)
          refreshData()
        } else {
          message.error(
            getApiErrorMessage(
              data,
              "Uh oh, something went wrong reopening meet declarations",
            ),
          )
        }
      })
  }

  const statusLabel = (() => {
    if (isCurrentlyOpen) {
      if (deadlineLocal) {
        return `Open until ${formatForDisplay(
          deadlineLocal,
          "M/D h:mm a",
        )}`
      }
      return "Open to parents"
    }
    if (deadlineLocal) {
      return `Closed as of ${formatForDisplay(
        deadlineLocal,
        "M/D h:mm a",
      )}`
    }
    return "Not yet open to parents"
  })()

  return (
    <>
      <div
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: 8,
          gap: 8,
        }}
      >
        <Switch
          checked={isCurrentlyOpen}
          onChange={handleToggleChange}
          disabled={declarationsLocked}
        />
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 6,
            lineHeight: 1.2,
            fontSize: 11,
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 12 }}>Meet Declarations</span>
          <span>{statusLabel}</span>
          {hasDeadline && (
            declarationsLocked ? (
              <span
                style={{
                  fontSize: 11,
                  color: "#999",
                }}
              >
                Edit Meet Declarations Settings
              </span>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (isCurrentlyOpen && deadlineLocal) {
                    setCloseDate(deadlineLocal)
                    setEditModalVisible(true)
                  } else {
                    setCloseDate(null)
                    setReopenModalVisible(true)
                  }
                }}
                style={{
                  border: "none",
                  background: "none",
                  padding: 0,
                  margin: 0,
                  cursor: "pointer",
                  color: "#1677ff",
                  textDecoration: "underline",
                  fontSize: 11,
                }}
              >
                Edit Meet Declarations Settings
              </button>
            )
          )}
        </div>
      </div>

      {/* Open declarations modal (initial open) */}
      <Modal
        title="Open Meet Declarations"
        open={openModalVisible}
        okText="Save meet declarations"
        onOk={handleOpenOk}
        onCancel={() => setOpenModalVisible(false)}
      >
        <Typography.Paragraph style={{ marginBottom: 8 }}>
          Have parents declare their swimmers for this meet in the Clubhouse.
          You still control final entries and relays.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Choose what parents can do, set a declarations deadline, and decide
          how we notify families.
        </Typography.Paragraph>
        <div style={{ marginBottom: 8 }}>
          <strong>Parent options</strong>
        </div>
        <Checkbox
          checked={allowIndividualEntries}
          onChange={(e) => setAllowIndividualEntries(e.target.checked)}
        >
          Allow parents to enter their swimmers in individual events
        </Checkbox>
        <br />
        <Checkbox
          checked={allowRelayAvailability}
          onChange={(e) => setAllowRelayAvailability(e.target.checked)}
        >
          Allow parents/guardians to mark relay availability
        </Checkbox>
        <br />
        <Checkbox
          checked={allowComments}
          onChange={(e) => setAllowComments(e.target.checked)}
        >
          Allow comments from parents/guardians to coaches
        </Checkbox>
        <br />
        <br />
        <div style={{ marginBottom: 8 }}>
          <strong>Declarations deadline</strong>
        </div>
        <DatePicker
          format="MM-DD-YYYY hh:mm a"
          showTime={{ format: "hh:mm a", minuteStep: 15 } as any}
          showNow={false}
          placeholder="Select Meet Entry Closing Date Time"
          style={{ width: "400px" }}
          disabledDate={disabledDateForPicker}
          value={closeDate}
          onChange={(val) => setCloseDate(val || null)}
        />
        <div style={{ marginTop: 4 }}>
          <Typography.Text type="secondary">
            Parents can respond until this time. Choose a deadline at least one
            hour from now so your staff has time to review entries.
          </Typography.Text>
        </div>
        <Divider />
        <div style={{ marginBottom: 8 }}>
          <strong>Email notifications</strong>
        </div>
        <Checkbox
          checked={sendEmailNow}
          onChange={(e) => setSendEmailNow(e.target.checked)}
        >
          Send announcement email to parents now (recommended)
        </Checkbox>
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="secondary">
            Recommended so parents know about this meet and deadline.
          </Typography.Text>
        </div>
        <br />
        <Checkbox
          checked={enableReminder}
          onChange={(e) => setEnableReminder(e.target.checked)}
        >
          Send automatic reminder before the deadline
        </Checkbox>
        {enableReminder && (
          <div style={{ marginLeft: 24, marginTop: 4 }}>
            <span style={{ marginRight: 8 }}>Reminder timing:</span>
            <Select
              size="small"
              style={{ width: "fit-content" }}
              value={reminderOffsetHours}
              onChange={setReminderOffsetHours}
              options={[
                { value: 24, label: "24 hours before" },
                { value: 48, label: "48 hours before (recommended)" },
                { value: 72, label: "72 hours before" },
              ]}
            />
          </div>
        )}
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="secondary">
            In addition to this automated reminder, you can manually send up to
            3 email reminders to parents/guardians who have not yet responded.
          </Typography.Text>
        </div>
        {renderSenderAndAdminCopySection()}
      </Modal>

      {/* In-progress modal (edit existing future deadline) */}
      <Modal
        title="Update Meet Declarations"
        open={editModalVisible}
        okText="Save meet declarations"
        onOk={handleEditOk}
        onCancel={() => setEditModalVisible(false)}
      >
        <Typography.Paragraph style={{ marginBottom: 8 }}>
          Update what parents can do for this meet or adjust the declarations
          deadline. Existing responses will remain in place.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Changes here update both the Clubhouse experience for parents and how
          we notify them.
        </Typography.Paragraph>
        <div style={{ marginBottom: 8 }}>
          <strong>Parent options</strong>
        </div>
        <Checkbox
          checked={allowIndividualEntries}
          onChange={(e) => setAllowIndividualEntries(e.target.checked)}
        >
          Allow parents to enter their swimmers in individual events
        </Checkbox>
        <br />
        <Checkbox
          checked={allowRelayAvailability}
          onChange={(e) => setAllowRelayAvailability(e.target.checked)}
        >
          Allow parents/guardians to mark relay availability
        </Checkbox>
        <br />
        <Checkbox
          checked={allowComments}
          onChange={(e) => setAllowComments(e.target.checked)}
        >
          Allow comments from parents/guardians to coaches
        </Checkbox>
        <br />
        <div style={{ marginTop: 8, marginBottom: 8 }}>
          <strong>Declarations deadline</strong>
        </div>
        <DatePicker
          showTime={{ format: "hh:mm a", minuteStep: 15 } as any}
          showNow={false}
          placeholder="Select Meet Entry Closing Date Time"
          style={{ width: "400px" }}
          disabledDate={disabledDateForPicker}
          value={closeDate}
          format="MM-DD-YYYY hh:mm a"
          onChange={(val) => setCloseDate(val || null)}
        />
        <div style={{ marginTop: 4 }}>
          <Typography.Text type="secondary">
            Parents can respond until this time. Choose a deadline at least one
            hour from now so your staff has time to review entries.
          </Typography.Text>
        </div>
        <Divider />
        <div style={{ marginBottom: 8 }}>
          <strong>Email notifications</strong>
        </div>
        <Checkbox
          checked={sendEmailNow}
          onChange={(e) => setSendEmailNow(e.target.checked)}
        >
          Send updated email about these changes (recommended)
        </Checkbox>
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="secondary">
            Recommended so parents know about any updated details.
          </Typography.Text>
        </div>
        <br />
        <Checkbox
          checked={enableReminder}
          onChange={(e) => setEnableReminder(e.target.checked)}
        >
          Send automatic reminder before the deadline
        </Checkbox>
        {enableReminder && (
          <div style={{ marginLeft: 24, marginTop: 4 }}>
            <span style={{ marginRight: 8 }}>Reminder timing:</span>
            <Select
              size="small"
              style={{ width: "fit-content" }}
              value={reminderOffsetHours}
              onChange={setReminderOffsetHours}
              options={[
                { value: 24, label: "24 hours before" },
                { value: 48, label: "48 hours before (recommended)" },
                { value: 72, label: "72 hours before" },
              ]}
            />
          </div>
        )}
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="secondary">
            In addition to this automated reminder, you can manually send up to
            3 email reminders to parents/guardians who have not yet responded.
          </Typography.Text>
        </div>
        {renderSenderAndAdminCopySection()}
      </Modal>

      {/* Reopen modal (previously closed) */}
      <Modal
        title="Reopen Meet Declarations"
        open={reopenModalVisible}
        okText="Save and reopen declarations"
        onOk={handleReopenOk}
        onCancel={() => setReopenModalVisible(false)}
      >
        <Typography.Paragraph style={{ marginBottom: 8 }}>
          Meet declarations and entries are currently closed to parents for
          this meet. Reopen them to allow families to update their responses
          again.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          You can adjust parent options, set a new deadline, and choose how we
          notify families.
        </Typography.Paragraph>
        <div style={{ marginBottom: 8 }}>
          <strong>Parent options</strong>
        </div>
        <Checkbox
          checked={allowIndividualEntries}
          onChange={(e) => setAllowIndividualEntries(e.target.checked)}
        >
          Allow parents to enter their swimmers in individual events
        </Checkbox>
        <br />
        <Checkbox
          checked={allowRelayAvailability}
          onChange={(e) => setAllowRelayAvailability(e.target.checked)}
        >
          Allow parents/guardians to mark relay availability
        </Checkbox>
        <br />
        <Checkbox
          checked={allowComments}
          onChange={(e) => setAllowComments(e.target.checked)}
        >
          Allow comments from parents/guardians to coaches
        </Checkbox>
        <br />
        <div style={{ marginTop: 8, marginBottom: 8 }}>
          <strong>Declarations deadline</strong>
        </div>
        <DatePicker
          showTime={{ format: "hh:mm a", minuteStep: 15 } as any}
          showNow={false}
          placeholder="Select Meet Entry Closing Date Time"
          style={{ width: "400px" }}
          disabledDate={disabledDateForPicker}
          value={closeDate}
          format="MM-DD-YYYY hh:mm a"
          onChange={(val) => setCloseDate(val || null)}
        />
        <div style={{ marginTop: 4 }}>
          <Typography.Text type="secondary">
            Parents can respond until this time. Choose a deadline at least one
            hour from now so your staff has time to review entries.
          </Typography.Text>
        </div>
        <Divider />
        <div style={{ marginBottom: 8 }}>
          <strong>Email notifications</strong>
        </div>
        <Checkbox
          checked={sendEmailNow}
          onChange={(e) => setSendEmailNow(e.target.checked)}
        >
          Send updated email about these changes (recommended)
        </Checkbox>
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="secondary">
            Recommended so parents know declarations have been reopened and
            when the new deadline is.
          </Typography.Text>
        </div>
        <br />
        <Checkbox
          checked={enableReminder}
          onChange={(e) => setEnableReminder(e.target.checked)}
        >
          Send automatic reminder before the deadline
        </Checkbox>
        {enableReminder && (
          <div style={{ marginLeft: 24, marginTop: 4 }}>
            <span style={{ marginRight: 8 }}>Reminder timing:</span>
            <Select
              size="small"
              style={{ width: "fit-content" }}
              value={reminderOffsetHours}
              onChange={setReminderOffsetHours}
              options={[
                { value: 24, label: "24 hours before" },
                { value: 48, label: "48 hours before (recommended)" },
                { value: 72, label: "72 hours before" },
              ]}
            />
          </div>
        )}
        <div style={{ marginLeft: 24, marginTop: 4 }}>
          <Typography.Text type="secondary">
            In addition to this automated reminder, you can manually send up to
            3 email reminders to parents/guardians who have not yet responded.
          </Typography.Text>
        </div>
        {renderSenderAndAdminCopySection()}
      </Modal>
    </>
  )
}
